import { CaretDownIcon } from '@phosphor-icons/react';
import { CodeBlock } from '@whistler-ui/k2/components/CodeBlock';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { Paragraph } from '@whistler-ui/k2/components/Typography';
import { sortObject } from '@whistler-ui/k2/helpers';
import { cn } from '@whistler-ui/k2/helpers/classBuilders';
import { useClassHelper } from '@whistler-ui/k2/hooks';
import { motion } from 'motion/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HeaderType, TransactionMetadata } from '../types';

// Headers whose value renders as a copyable CodeBlock instead of plain text
// (e.g. `location`, so the redirect URL is easy to copy). Keys are matched
// case-insensitively, so entries must be lowercase.
const COPYABLE_HEADER_KEYS = ['location'];

interface Props {
  headers: HeaderType & TransactionMetadata;
  componentName: string;
  testId: string;
}

export const MoreHeaders = ({ headers, componentName, testId }: Props) => {
  const c = useClassHelper(componentName);
  const { t } = useTranslation();
  const [viewMore, setViewMore] = useState(false);

  // Copy the headers object
  const extraHeaders = { ...headers };

  // Delete the unwanted headers
  // Be careful, sometimes it have underscore instead of dash
  delete extraHeaders['correlation_id'];
  delete extraHeaders['correlation-id'];

  delete extraHeaders['transaction_id'];
  delete extraHeaders['transaction-id'];

  const isExtraHeaders = Object.keys(extraHeaders).length > 0;
  const sortedHeaders = sortObject(extraHeaders);

  if (isExtraHeaders) {
    return (
      <div className={c('__more')}>
        <motion.div
          initial={false}
          animate={viewMore ? 'open' : 'collapsed'}
          variants={{
            open: { height: 'auto', opacity: 1 },
            collapsed: { height: 0, opacity: 0 },
          }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          className={c(
            '__more-content',
            viewMore ? '__more-content--active' : '',
          )}
          data-testid={testId}
        >
          {Object.entries(sortedHeaders).map(([key, value]) => {
            if (COPYABLE_HEADER_KEYS.includes(key.toLowerCase()) && value) {
              return (
                <Stack
                  key={key}
                  direction="row"
                  align="center"
                  gap={2}
                  data-testid={`${testId}-copy-${key}`}
                >
                  <Paragraph>
                    <b>{key}:</b>
                  </Paragraph>
                  <Stack.Item flex="1" className="min-w-0">
                    <CodeBlock
                      value={[String(value)]}
                      actionsPosition="overlay-middle"
                      hideLineNumbers
                    />
                  </Stack.Item>
                </Stack>
              );
            }

            return (
              <div key={key}>
                <Paragraph>
                  <b>{key}:</b> {value}
                </Paragraph>
              </div>
            );
          })}
        </motion.div>
        <button
          type="button"
          className={cn(
            c('__more-btn', viewMore ? '__more-btn--active' : ''),
            'focus-visible:focus-ring',
          )}
          aria-expanded={viewMore}
          onClick={() => setViewMore(prev => !prev)}
          data-cy={testId ? `${testId}-more-info-toggle` : undefined}
        >
          <CaretDownIcon
            size={14}
            aria-hidden="true"
            className={c('__more-btn-caret')}
          />
          <span>{viewMore ? t('Hide details') : t('Show details')}</span>
        </button>
      </div>
    );
  }

  return null;
};
